import { showToast } from "@certn/cascadia";
import { Formik } from "formik";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { array, object, string } from "yup";

import { RequestDetailsModal } from "./RequestDetailsModal";
import { RequestTypeModal } from "./RequestTypeModal";
import { useFormSubmit } from "../../../../hooks/api/useSubmit";
import { ActionListItemWithModal } from "../ActionListItemWithModal";
import { DeleteAccountWarningModal } from "../DeleteAccountWarningModal";

export function RequestPersonalData() {
  const { t } = useTranslation();

  const title = t("Personal data and account deletion");
  const [showDetails, setShowDetails] = useState<boolean>();
  const [showWarning, setShowWarning] = useState<boolean>();
  const navigate = useNavigate();

  const submitForm = useFormSubmit(
    "/api/internal/applicant/request-account-data-delete-account/",
    {
      onSuccess: () =>
        showToast(t("Request sent"), {
          type: "info",
        }),
      raiseError: true,
    }
  );

  const schema = object().shape({
    request_types: array()
      .min(1, t("Please select at least one option."))
      .of(string()),

    request_details: string().required().label(t("Request details")),
  });

  return (
    <ActionListItemWithModal
      testID="help-and-privacy-personal-data-request-button"
      title={title}
      description={t(
        "Request, delete, or make access changes to your personal data"
      )}
      link={t("Delete")}
    >
      {({ close }) => {
        const handleClose = () => {
          setShowDetails(false);
          setShowWarning(false);
          close();
        };
        return (
          <Formik
            initialValues={{
              request_types: [] as string[],
              request_details: "",
              supporting_documents: [],
            }}
            validationSchema={schema}
            onSubmit={async (values, helpers) => {
              try {
                const supportingDocuments = values.supporting_documents.map(
                  ({ content }) => content
                );
                await submitForm(
                  { ...values, supporting_documents: supportingDocuments },
                  helpers
                );
                handleClose();
              } catch (e) {
                console.error('Error in "Personal data request":', e);
                showToast(t("Something went wrong, try again"), {
                  type: "error",
                });
              }
            }}
          >
            {({ handleSubmit, values, setErrors }) => {
              return showWarning ? (
                <DeleteAccountWarningModal
                  onSubmit={() => {
                    setShowDetails(true);
                    setShowWarning(false);
                  }}
                  onBack={() => setShowWarning(false)}
                  onClose={handleClose}
                />
              ) : showDetails ? (
                <RequestDetailsModal
                  onSubmit={() => {
                    handleSubmit();
                    if (values.request_types.includes("DELETE_DATA")) {
                      navigate("/account-deleted");
                    }
                  }}
                  onBack={() => setShowDetails(false)}
                  onClose={handleClose}
                />
              ) : (
                <RequestTypeModal
                  onNext={() => {
                    if (values.request_types.length === 0) {
                      setErrors({
                        request_types: t("Please select at least one option."),
                      });
                      return;
                    }
                    if (values.request_types.includes("DELETE_DATA")) {
                      setShowWarning(true);
                      return;
                    }
                    setShowDetails(true);
                  }}
                  onClose={handleClose}
                />
              );
            }}
          </Formik>
        );
      }}
    </ActionListItemWithModal>
  );
}
